import { Avatar, Button, Column, Row, Typography } from '@tempmee/react';
import { CenteredSpinner } from '~/commonComponents/CenteredSpinner';
import { ScrollArea } from '~/commonComponents';
import { colors } from '@tempmee/tailwind';
import ConfirmPopup from '~/components/shared/ConfirmPopup';
import { InterviewRow } from './InterviewRow';
import { InviteSuccessView } from './InviteSuccessView';
import { useInviteToExistingInterviewModal } from './useInviteToExistingInterviewModal';

interface IInviteToExistingInterviewModalProps {
  isOpen: boolean;
  jobPostingId: string;
  officeId: string;
  professionalId: string;
  onClose: () => void;
}

export const InviteToExistingInterviewModal = ({
  isOpen,
  jobPostingId,
  officeId,
  professionalId,
  onClose,
}: IInviteToExistingInterviewModalProps) => {
  const {
    interviews,
    isLoading,
    isLoadingInvite,
    isSuccessInvite,
    professional,
    selectedInterviewIds,
    handleInvite,
    handleToggle,
  } = useInviteToExistingInterviewModal(isOpen, jobPostingId, professionalId, officeId);

  const isDisabled = selectedInterviewIds.length === 0;

  if (!isOpen) {
    return null;
  }

  if (isSuccessInvite) {
    return (
      <InviteSuccessView
        professional={professional}
        selectedCount={selectedInterviewIds.length}
        onClose={onClose}
      />
    );
  }

  return (
    <ConfirmPopup modalStyle={{ height: 684, width: 500 }} closePopup={onClose} showClosePopupIcon>
      {isLoading ? (
        <CenteredSpinner />
      ) : (
        <>
          <Column align="center" justify="center" gap={4} style={{ marginBottom: 20 }}>
            <Avatar
              size="xl"
              src={professional?.profile_image || undefined}
              alt={`${professional?.full_name} profile picture`}
            />
            <Typography as="span" color={colors.neutral[500]}>
              Select the interviews you&apos;d like to invite{' '}
              <strong>{professional?.full_name}</strong> to.
            </Typography>
          </Column>
          <ScrollArea style={{ flex: 1, width: 424 }}>
            <Column gap={2} py={1} px={3}>
              {interviews.length === 0 ? (
                <Typography as="span" color={colors.neutral[500]} textAlign="center">
                  No available interviews found.
                </Typography>
              ) : (
                interviews.map((interview) => (
                  <InterviewRow
                    key={interview.onsiteInterviewId}
                    {...interview}
                    isInvited={interview.isInvited}
                    isSelected={selectedInterviewIds.includes(interview.onsiteInterviewId)}
                    onToggle={handleToggle}
                  />
                ))
              )}
            </Column>
          </ScrollArea>
          <Row align="center" justify="center" gap={4} className="mt-4">
            <Button variant="secondary" size="md" onClick={onClose}>
              Go Back
            </Button>
            <Button
              type="button"
              size="md"
              onClick={handleInvite}
              disabled={isDisabled}
              isLoading={isLoadingInvite}
            >
              Invite
            </Button>
          </Row>
        </>
      )}
    </ConfirmPopup>
  );
};
